import { useFavoriteTab } from '../../hooks/useFavoriteTab';
// frontend/src/pages/administration/ApiCustomers.tsx
import { useState, useEffect, useCallback } from 'react';
import { useToast } from '../../contexts/ToastContext';
import LookupSearchField from '../../components/tablemaint/LookupSearchField';
import apiCustomersService, {
  ApiCustomer,
  ApiKeyReveal,
  CustomerApiLog,
  LookupItem,
  RefField,
} from '../../services/apiCustomers.service';
import ModalHeader from '../../components/ui/ModalHeader';
import { useModalDrag } from '../../hooks/useModalDrag';

const REF_FIELDS: { value: RefField; label: string }[] = [
  { value: 'billto', label: 'Bill To' },
  { value: 'customercode', label: 'Customer' },
  { value: 'brokercode', label: 'Broker / Forwarder' },
];

const refLabel = (f: string) => REF_FIELDS.find((r) => r.value === f)?.label ?? f;

const ACTIVITY_STATUSES = ['success', 'not_found', 'disabled', 'unauthorized', 'bad_request', 'error'];

const statusBadgeClass = (s: string) => {
  if (s === 'success') return 'bg-green-100 text-green-700';
  if (s === 'unauthorized' || s === 'error') return 'bg-red-100 text-red-700';
  if (s === 'not_found' || s === 'bad_request') return 'bg-yellow-100 text-yellow-700';
  return 'bg-gray-100 text-gray-700';
};

export default function ApiCustomers() {
  const { showToast } = useToast();

  const [tab, setTab] = useState<'callers' | 'activity'>('callers');
  useFavoriteTab(tab, setTab, ['callers', 'activity']);

  const [customers, setCustomers] = useState<ApiCustomer[]>([]);
  const [loading, setLoading] = useState(true);

  // Create modal
  const [showCreate, setShowCreate] = useState(false);
  const [integrations, setIntegrations] = useState<string[]>([]);
  const [form, setForm] = useState({ customer_code: '', name: '', notes: '' });
  const {
    panelRef: createPanelRef,
    panelStyle: createPanelStyle,
    dragHandleProps: createDragHandleProps,
  } = useModalDrag(showCreate);

  // One-time key reveal modal
  const [revealed, setRevealed] = useState<ApiKeyReveal | null>(null);
  const {
    panelRef: revealPanelRef,
    panelStyle: revealPanelStyle,
    dragHandleProps: revealDragHandleProps,
  } = useModalDrag(revealed !== null);

  // Inline account-mapping panel
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [addField, setAddField] = useState<RefField>('billto');
  const [lookupText, setLookupText] = useState('');

  // Activity tab
  const [activity, setActivity] = useState<CustomerApiLog[]>([]);
  const [activityStatus, setActivityStatus] = useState('');
  const [activityLoading, setActivityLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setCustomers(await apiCustomersService.list());
    } catch (e: any) {
      showToast({ message: e.response?.data?.detail || 'Error loading API customers', type: 'error' });
    } finally {
      setLoading(false);
    }
    // The integrations list only feeds the Add Caller dropdown — a failure
    // here (endpoint not deployed/granted yet) must never blank the callers.
    try {
      setIntegrations(await apiCustomersService.integrations());
    } catch {
      setIntegrations([]);
    }
  }, [showToast]);

  useEffect(() => {
    load();
  }, [load]);

  const loadActivity = useCallback(async () => {
    setActivityLoading(true);
    try {
      setActivity(await apiCustomersService.activity({ status: activityStatus || undefined, limit: 200 }));
    } catch (e: any) {
      showToast({ message: e.response?.data?.detail || 'Error loading activity', type: 'error' });
    } finally {
      setActivityLoading(false);
    }
  }, [showToast, activityStatus]);

  useEffect(() => {
    if (tab === 'activity') loadActivity();
  }, [tab, loadActivity]);

  const selected = customers.find((c) => c.id === selectedId) || null;

  // Codes a new caller can still be created for: integrations deployed in the
  // backend that don't already have a caller row.
  const availableCodes = integrations.filter(
    (code) => !customers.some((c) => c.customer_code === code)
  );

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.customer_code.trim() || !form.name.trim()) {
      showToast({ message: 'Code and name are required', type: 'error' });
      return;
    }
    try {
      const result = await apiCustomersService.create({
        customer_code: form.customer_code.trim(),
        name: form.name.trim(),
        notes: form.notes.trim() || undefined,
      });
      setShowCreate(false);
      setForm({ customer_code: '', name: '', notes: '' });
      setRevealed(result);
      load();
    } catch (e: any) {
      showToast({ message: e.response?.data?.detail || 'Error creating caller', type: 'error' });
    }
  };

  const handleRotate = async (c: ApiCustomer) => {
    if (!confirm(`Rotate the API key for "${c.name}"? The current key stops working immediately.`)) return;
    try {
      setRevealed(await apiCustomersService.rotate(c.id));
      load();  // refresh so key_rotated_by/_at aren't stale until next reload
    } catch (e: any) {
      showToast({ message: e.response?.data?.detail || 'Error rotating key', type: 'error' });
    }
  };

  const handleToggleEnabled = async (c: ApiCustomer) => {
    try {
      await apiCustomersService.update(c.id, { enabled: !c.enabled });
      showToast({ message: c.enabled ? 'Caller disabled' : 'Caller enabled', type: 'success' });
      load();
    } catch (e: any) {
      showToast({ message: e.response?.data?.detail || 'Error updating caller', type: 'error' });
    }
  };

  const handleAddAccount = async (item: LookupItem) => {
    if (selectedId == null) return;
    try {
      await apiCustomersService.addAccount(selectedId, { ref_field: addField, ref_recno: item.recno });
      setLookupText('');
      showToast({ message: `Added ${refLabel(addField)}: ${item.name}`, type: 'success' });
      load();
    } catch (e: any) {
      showToast({ message: e.response?.data?.detail || 'Error adding mapping', type: 'error' });
    }
  };

  const handleRemoveAccount = async (c: ApiCustomer, accountId: number) => {
    try {
      await apiCustomersService.removeAccount(c.id, accountId);
      showToast({ message: 'Mapping removed', type: 'success' });
      load();
    } catch (e: any) {
      showToast({ message: e.response?.data?.detail || 'Error removing mapping', type: 'error' });
    }
  };

  const copyKey = async () => {
    if (!revealed) return;
    try {
      await navigator.clipboard.writeText(revealed.api_key);
      showToast({ message: 'Key copied to clipboard', type: 'success' });
    } catch {
      showToast({ message: 'Copy failed — select and copy manually', type: 'error' });
    }
  };

  const fmt = (s?: string | null) => (s ? new Date(s).toLocaleString() : '-');

  return (
    <div className="p-4 max-w-6xl mx-auto">
      <div className="flex justify-between items-center mb-2">
        <h1 className="text-2xl font-bold">API Customers</h1>
        {tab === 'callers' && (
          <button
            onClick={() => setShowCreate(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Add Caller
          </button>
        )}
      </div>
      <p className="text-gray-600 dark:text-gray-400 mb-4">
        External callers (e.g. freight forwarders) that authenticate the /integrations APIs with an
        API key. A caller only returns data for the CMS accounts it's mapped to.
      </p>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b border-gray-200 dark:border-gray-700">
        {(['callers', 'activity'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 ${
              tab === t
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-muted hover:text-gray-700'
            }`}
          >
            {t === 'callers' ? 'Callers' : 'Activity'}
          </button>
        ))}
      </div>

      {tab === 'callers' && (
        <>
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : (
            <div className="bg-panel rounded-lg shadow overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    {['Code', 'Name', 'Status', 'Accounts', 'Created', 'Created By', 'Actions'].map((h) => (
                      <th
                        key={h}
                        className={`px-4 py-3 text-sm font-medium text-gray-600 dark:text-gray-400 ${
                          h === 'Actions' ? 'text-right' : 'text-left'
                        }`}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {customers.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-8 text-center text-muted">
                        No API customers yet
                      </td>
                    </tr>
                  ) : (
                    customers.map((c) => (
                      <tr key={c.id} className="hover:bg-highlight">
                        <td className="px-4 py-3 text-sm">{c.customer_code}</td>
                        <td className="px-4 py-3">{c.name}</td>
                        <td className="px-4 py-3">
                          <span
                            className={`px-2 py-1 rounded text-xs font-medium ${
                              c.enabled ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                            }`}
                          >
                            {c.enabled ? 'Enabled' : 'Disabled'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => setSelectedId(selectedId === c.id ? null : c.id)}
                            className="text-blue-600 hover:text-blue-800 text-sm"
                          >
                            {c.accounts.length} mapping{c.accounts.length === 1 ? '' : 's'}
                            {selectedId === c.id ? ' ▲' : ' ▼'}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-muted text-sm">{fmt(c.created_at)}</td>
                        <td
                          className="px-4 py-3 text-muted text-sm"
                          title={
                            c.key_rotated_by
                              ? `Key last rotated by ${c.key_rotated_by}${c.key_rotated_at ? ' on ' + new Date(c.key_rotated_at).toLocaleString() : ''}`
                              : undefined
                          }
                        >
                          {c.created_by || '-'}
                        </td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          <button
                            onClick={() => handleRotate(c)}
                            className="text-blue-600 hover:text-blue-800 text-sm mr-3"
                          >
                            Rotate Key
                          </button>
                          <button
                            onClick={() => handleToggleEnabled(c)}
                            className={`text-sm ${
                              c.enabled ? 'text-red-600 hover:text-red-800' : 'text-green-600 hover:text-green-800'
                            }`}
                          >
                            {c.enabled ? 'Disable' : 'Enable'}
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Account-mapping panel */}
          {selected && (
            <div className="mt-6 bg-panel rounded-lg shadow p-4">
              <h2 className="text-lg font-bold mb-1">Account mappings — {selected.name}</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                This caller's GET requests only return orders whose scoping field matches one of these
                records. With no mappings, it returns nothing (deny-by-default).
              </p>

              <div className="flex flex-wrap gap-2 mb-4">
                {selected.accounts.length === 0 && (
                  <span className="text-sm text-muted">No mappings yet.</span>
                )}
                {selected.accounts.map((a) => (
                  <span
                    key={a.id}
                    className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gray-100 dark:bg-gray-700 text-sm"
                  >
                    <span className="font-medium">{refLabel(a.ref_field)}:</span>
                    <span>{a.ref_name || `#${a.ref_recno}`}</span>
                    <span className="text-gray-400">#{a.ref_recno}</span>
                    <button
                      onClick={() => handleRemoveAccount(selected, a.id)}
                      className="text-red-500 hover:text-red-700 font-bold"
                      title="Remove mapping"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
                <div>
                  <label className="block text-sm font-medium text-label mb-1">Type</label>
                  <select
                    value={addField}
                    onChange={(e) => {
                      setAddField(e.target.value as RefField);
                      setLookupText('');
                    }}
                    className="border rounded px-3 py-2"
                  >
                    {REF_FIELDS.map((f) => (
                      <option key={f.value} value={f.value}>
                        {f.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex-1">
                  <LookupSearchField<LookupItem>
                    label={`Search ${refLabel(addField)} by name or recno`}
                    displayValue={lookupText}
                    onType={setLookupText}
                    onSelect={handleAddAccount}
                    search={(q) => apiCustomersService.lookup(addField, q)}
                    renderItem={(it) => (
                      <div className="text-sm">
                        <span className="font-medium">{it.name}</span>
                        {(it.city || it.state) && (
                          <span className="text-gray-500"> · {[it.city, it.state].filter(Boolean).join(', ')}</span>
                        )}
                        <span className="text-gray-400"> · #{it.recno}</span>
                      </div>
                    )}
                    itemValue={(it) => String(it.recno)}
                    itemLabel={(it) => it.name}
                    placeholder="Name or recno (2+ characters)…"
                  />
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {tab === 'activity' && (
        <div>
          <div className="flex items-center gap-3 mb-4">
            <label className="text-sm font-medium text-label">Status</label>
            <select
              value={activityStatus}
              onChange={(e) => setActivityStatus(e.target.value)}
              className="border rounded px-3 py-2 text-sm"
            >
              <option value="">All</option>
              {ACTIVITY_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <button
              onClick={loadActivity}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded text-sm text-label hover:bg-highlight"
            >
              Refresh
            </button>
          </div>

          {activityLoading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : (
            <div className="bg-panel rounded-lg shadow overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    {['Time', 'Caller', 'Method', 'Endpoint', 'HTTP', 'Status', 'Ref', 'ms', 'Error'].map((h) => (
                      <th key={h} className="px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-400">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {activity.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="px-3 py-8 text-center text-muted">
                        No activity recorded
                      </td>
                    </tr>
                  ) : (
                    activity.map((row) => (
                      <tr key={row.id} className="hover:bg-highlight text-sm">
                        <td className="px-3 py-2 whitespace-nowrap text-muted">{fmt(row.received_at)}</td>
                        <td className="px-3 py-2">{row.customer_code || '-'}</td>
                        <td className="px-3 py-2">{row.method}</td>
                        <td className="px-3 py-2 text-xs">{row.endpoint}</td>
                        <td className="px-3 py-2">{row.http_status}</td>
                        <td className="px-3 py-2">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${statusBadgeClass(row.status)}`}>
                            {row.status}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-xs">{row.request_ref || '-'}</td>
                        <td className="px-3 py-2 text-muted">{row.duration_ms ?? '-'}</td>
                        <td className="px-3 py-2 text-red-600 dark:text-red-400 text-xs">{row.error_message || ''}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-scrim bg-opacity-50 flex items-center justify-center z-50">
          <div
            ref={createPanelRef}
            style={createPanelStyle}
            className="bg-panel rounded-lg shadow-lg w-full max-w-md mx-4"
          >
            <ModalHeader
              title="Add API Caller"
              onClose={() => setShowCreate(false)}
              dragHandleProps={createDragHandleProps}
            />
            <form onSubmit={handleCreate} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Integration</label>
                <select
                  value={form.customer_code}
                  onChange={(e) => setForm({ ...form, customer_code: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  required
                >
                  <option value="" disabled>
                    {availableCodes.length ? 'Select an integration…' : 'No integrations available'}
                  </option>
                  {availableCodes.map((code) => (
                    <option key={code} value={code}>
                      {code}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  {availableCodes.length
                    ? 'Only integrations deployed in this backend are listed; the key is scoped to the one you pick.'
                    : 'Every deployed integration already has a caller. Deploy a new integration first.'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Name</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Partner or company name"
                  className="w-full border rounded px-3 py-2"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Notes (optional)</label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  rows={2}
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowCreate(false)}
                  className="flex-1 px-4 py-2 border dark:border-gray-600 rounded text-label hover:bg-highlight"
                >
                  Cancel
                </button>
                <button type="submit" className="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* One-time key reveal modal */}
      {revealed && (
        <div className="fixed inset-0 bg-scrim bg-opacity-50 flex items-center justify-center z-50">
          <div
            ref={revealPanelRef}
            style={revealPanelStyle}
            className="bg-panel rounded-lg shadow-lg w-full max-w-lg mx-4"
          >
            <ModalHeader
              title={`API key for ${revealed.customer_code}`}
              onClose={() => setRevealed(null)}
              dragHandleProps={revealDragHandleProps}
            />
            <div className="p-6 space-y-4">
              <div className="rounded border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700 p-3 text-sm text-amber-800 dark:text-amber-200">
                This is the only time this key is shown. Copy it now and hand it to the customer
                securely — it cannot be retrieved again (only rotated).
              </div>
              <div className="flex gap-2">
                <input
                  readOnly
                  value={revealed.api_key}
                  onFocus={(e) => e.currentTarget.select()}
                  className="flex-1 border rounded px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900"
                />
                <button onClick={copyKey} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
                  Copy
                </button>
              </div>
              <div className="flex justify-end pt-2">
                <button
                  onClick={() => setRevealed(null)}
                  className="px-4 py-2 border dark:border-gray-600 rounded text-label hover:bg-highlight"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
